import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import Navbar from "../Navbar.jsx";
import Footer from "../Foooter.jsx";
import FooterMovil from "../FooterMovil.jsx";
import CarOverview from "../CarOverview.jsx";
import CarOverviewNotSpecialHome from "../CarOverviewNotSpecialHome";
import CarOverviewOferta from "../CarOverviewOferta.jsx";
import CarOverviewDestacado from "../CarOverviewDestacado.js";
import SearchBar from "../SearchBar.jsx";
import api from "../../utils/apiClient";
import "../../style/Home.css";
import "../../style/Stock.css";
import { useAuth } from "../../context/AuthContext.js";
import ImageCarousel from "../ImageCarousel.jsx";
import VideoContainer from "../VideoContainer.jsx";
import LogoGallery from "../LogoGalley.jsx";
import HomeDropdown from "../HomeDropdown.jsx";

// Snapshot of the car lists + active tab, keyed by the history entry
// (location.key). Lets a back-navigation rehydrate exactly what the user was
// looking at — including how deep they'd infinite-scrolled the inventory tab —
// instead of resetting to a fresh "Destacados" fetch. ScrollRestoration then
// restores the scroll offset against the now full-height content.
const homeCache = new Map();
const HOME_CACHE_MAX = 12;


const Home = () => {
  const location = useLocation();
  const navigationType = useNavigationType();

  // Resolve the snapshot to restore exactly once, during render. It's a pure
  // Map read — idempotent under StrictMode's double-invoked render and immune to
  // any later mutation of homeCache. Seeding the state below from it (lazy
  // useState) means the FIRST committed render already has the cached lists at
  // full height, so there is no empty-default frame for StrictMode's
  // setup→cleanup→setup cycle to snapshot, and nothing short for
  // ScrollRestoration to land against.
  const [restored] = useState(() =>
    navigationType === "POP" ? homeCache.get(location.key) || null : null
  );
  const hasUsableRestore = !!(
    restored && Array.isArray(restored.carros) && restored.carros.length > 0
  );

  const [carros, setCarros] = useState(() => restored?.carros ?? []);
  const [viewRecentCarros, setviewRecentCarros] = useState(() => restored?.viewRecentCarros ?? []);
  const [, setLoading] = useState(true);
  const [, setError] = useState(null);
  const { authToken } = useAuth();
  const [isofferta, setisofferta] = useState(() => (restored ? restored.isofferta : false));
  const [isDestacado, setisDestacado] = useState(() => (restored ? restored.isDestacado : true));
  const [isInventory, setisInventory] = useState(() => (restored ? restored.isInventory : false));
  const [isBuenEstado, setisBuenEstado] = useState(() => (restored ? restored.isBuenEstado : false));
  const [marcas, setMarcas] = useState([]);
  const [banners, setbanners] = useState([]);
  const [allmodelos, setAllModelos] = useState([]);

  const [selectedButton, setSelectedButton] = useState(() => restored?.selectedButton ?? "Destacados");

  const scrollRef = useRef(null);
  const scrollRefRecent = useRef(null);
  const scrollRefCars = useRef(null);
  const inventorySentinelRef = useRef(null);
  const inventoryOffsetRef = useRef(restored?.inventoryOffset ?? 0);
  const inventoryHasMoreRef = useRef(restored?.inventoryHasMore ?? false);
  const inventoryLoadingRef = useRef(false);
  // True until a restored inventory list has finished re-rendering, so the
  // infinite-scroll observer doesn't append an extra page mid scroll-restore.
  const restoringRef = useRef(hasUsableRestore);

  // Always holds the latest list/tab state so the save effect can snapshot it.
  const latestStateRef = useRef(null);
  latestStateRef.current = {
    selectedButton,
    isDestacado,
    isofferta,
    isInventory,
    isBuenEstado,
    carros,
    viewRecentCarros,
    inventoryOffset: inventoryOffsetRef.current,
    inventoryHasMore: inventoryHasMoreRef.current,
  };

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -500, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 500, behavior: "smooth" });
    }
  };

  // Each horizontal car row has its own ref; scroll the one whose arrow was hit.
  const scrollByRef = (ref, dx) => {
    if (ref.current) {
      ref.current.scrollBy({ left: dx, behavior: "smooth" });
    }
  };

  const findofferta = async () => {
    try {
      const res = await api.get("findspecial/findoferta?limit=30");
      const carros = res.data.carros || [];
      setisDestacado(false);
      setisofferta(true);
      setisInventory(false);
      setisBuenEstado(false);
      setCarros(carros);
    } catch (error) {
      console.error("Error fetching ofertas:", error);
    }
  };

  const findBuenEstado = async () => {
    try {
      const res = await api.post("filter/?offset=0&limit=30", {
        condicion: "Buen Estado",
      });
      setisDestacado(false);
      setisofferta(false);
      setisInventory(false);
      setisBuenEstado(true);
      setCarros(res.data.carros || []);
    } catch (error) {
      console.error("Error fetching buen estado:", error);
    }
  };

  const findNew = async () => {
    try {
      const res = await api.get("findspecial/nuevoingreso?limit=30");
      setisDestacado(false);
      setisofferta(false);
      setisInventory(false);
      setisBuenEstado(false);
      setCarros(res.data.carros || []);
    } catch (error) {
      console.error("Error fetching nuevo ingreso:", error);
    }
  };

  const findStock = async () => {
    if (inventoryLoadingRef.current) return;
    inventoryLoadingRef.current = true;
    inventoryOffsetRef.current = 0;
    inventoryHasMoreRef.current = true;
    try {
      const res = await api.get("findspecial/paginated/0");
      setisDestacado(false);
      setisofferta(false);
      setisInventory(true);
      setisBuenEstado(false);
      const list = res.data.carros || [];
      setCarros(list);
      inventoryHasMoreRef.current = Boolean(res.data.hasMore);
      inventoryOffsetRef.current = list.length;
    } catch (error) {
      console.error("Error fetching inventario:", error);
    } finally {
      inventoryLoadingRef.current = false;
    }
  };

  const fetchMoreInventory = async () => {
    if (inventoryLoadingRef.current || !inventoryHasMoreRef.current) return;
    inventoryLoadingRef.current = true;
    try {
      const res = await api.get(`findspecial/paginated/${inventoryOffsetRef.current}`);
      const list = res.data.carros || [];
      setCarros((prev) => [...prev, ...list]);
      inventoryHasMoreRef.current = Boolean(res.data.hasMore);
      inventoryOffsetRef.current += list.length || 30;
    } catch (error) {
      console.error("Error loading more inventario:", error);
    } finally {
      inventoryLoadingRef.current = false;
    }
  };

  const findViewRecent = async () => {
    try {
      const res = await api.get("findspecial/recentbyuser");
      const data = res.data;
      setviewRecentCarros(data.carrosVistosReciente);
    } catch (error) {
      console.error("Error fetching recently viewed:", error);
    }
  };
  
  const getMarcas = async () => {
    try {
      const res = await api.get("marcasymodelos/marcaCantidad");
      setMarcas(res.data.map((carro) => carro.Nombre));
    } catch (error) {
      console.error("Error fetching marcas:", error);
    }
  };

  const getBanners = async () => {
    try {
      const res = await api.get("anuncios/");
      const imagedata = res.data;
      const imageUrls = imagedata.map((item) => item.Imagen);
      setbanners(imageUrls);
    } catch (error) {
      console.error("Error fetching banners:", error);
    }
  };

  const getModelosAll = async () => {
    try {
      const res = await api.get("marcasymodelos/modelo");
      const nombres = res.data.map((modelo) => modelo.Nombre);
      setAllModelos(nombres);
    } catch (error) {
      console.error("Error fetching modelos:", error);
    }
  };

  const fetchAllCarros = async () => {
    setLoading(true);
    try {
      const response = await api.get("findspecial/destacar?limit=30");
      setCarros(response.data.carros || []);
      setError(null);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to fetch data");
      setCarros([]); // Consider whether you want to reset carros on error
    }
    await setLoading(false);
  };

  // The lists/tab are already seeded from the snapshot via lazy useState, so
  // here we only need to fetch when there's nothing usable to restore (fresh
  // push, reload, or a cache miss/empty entry — hasUsableRestore is the
  // backstop that re-fetches Destacados rather than rendering an empty grid).
  useEffect(() => {
    if (!hasUsableRestore) fetchAllCarros();
    Promise.all([getBanners(), getMarcas(), getModelosAll()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Continuously snapshot the current lists + tab into homeCache under this
  // history entry, so a later Back restores it verbatim. No unmount cleanup:
  // because state is lazy-seeded from `restored`, the first write already holds
  // the good values, which keeps it safe under StrictMode's setup/cleanup/setup.
  // delete-then-set re-inserts the entry as most-recent for LRU eviction.
  useEffect(() => {
    homeCache.delete(location.key);
    homeCache.set(location.key, latestStateRef.current);
    if (homeCache.size > HOME_CACHE_MAX) {
      homeCache.delete(homeCache.keys().next().value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, selectedButton, isDestacado, isofferta, isInventory, carros, viewRecentCarros]);

  // Don't refetch Vistos Recientemente when we just restored it — the refetch
  // would change the row's height and disturb the freshly-restored scroll.
  useEffect(() => {
    if (authToken && (!restored || (restored.viewRecentCarros?.length ?? 0) === 0)) {
      findViewRecent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  // Lift the infinite-scroll suppression once the restored deep scroll has had
  // a couple of frames to settle. The list is already full-height (lazy-seeded),
  // so ScrollRestoration lands the container on its first rAF; we just wait past
  // it so the observer doesn't append a page and churn the clamp loop.
  useEffect(() => {
    if (!restoringRef.current) return undefined;
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        restoringRef.current = false;
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, []);

  useEffect(() => {
    if (!isInventory) return undefined;
    const sentinel = inventorySentinelRef.current;
    const scrollEl = scrollRefCars.current;
    if (!sentinel) return undefined;
    const root = scrollEl && scrollEl.scrollHeight > scrollEl.clientHeight ? scrollEl : null;
    const observer = new IntersectionObserver(
      (entries) => {
        // Skip while we're still settling a restored deep scroll, so we don't
        // append an extra page and churn ScrollRestoration's clamp loop.
        if (restoringRef.current) return;
        if (entries[0]?.isIntersecting) {
          fetchMoreInventory();
        }
      },
      { root, rootMargin: "200px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInventory, carros.length]);

  const handleButtonClick = (button, originalFunction) => {
    setSelectedButton(button);
    originalFunction();
  };

  const scrollToSection = (id, offset = 0) => {
    const section = document.getElementById(id);
    if (section) {
      const sectionPosition =
        section.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = sectionPosition - window.innerHeight * offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <>
      <Navbar />
      <center>
      <div className="div-home">
        <span>
          <HomeDropdown scrollToSection={scrollToSection}/>
        </span>
      </div>
    </center>
      <center>
        <div className="search-bar-home">
          <div className="search-presentation">
            <p className="search-ttl">
              EL PRIMER Y MEJOR SITIO DE AUTOS IMPORTADOS EN HONDURAS
            </p>
            <div className="search-actions">
              <SearchBar
                marcas={marcas}
                allmodelos={allmodelos}
                className="hero-variant"
              />
            </div>
          </div>
        </div>
      </center>
      {/* <center>
        <div className="filter-buttons">
          <button className="home-btn" onClick={() => scrollToSection('to-logo', 0.2)}>Comprar por marca</button>
          <button className="home-btn"><a href="" className="ref-home">Buen estado</a></button>
          <button className="home-btn"><a href="/stock" className="ref-home">Busqueda avanzada</a></button>
        </div>
      </center> */}
      {authToken && viewRecentCarros?.length > 0 && (
        <div className="offers">
          <p className="search-title">VISTOS RECIENTEMENTE</p>
          <button className="arrow-h left-arrow" onClick={() => scrollByRef(scrollRefRecent, -500)}>
            <img
              src="icons/arrow-left.png"
              alt="flecha izquierda"
              className="arrow-img"
            />
          </button>
          <div className="scroll-type" ref={scrollRefRecent} data-scroll-key="home-recent">
            {viewRecentCarros.map((carro) => (
              <CarOverviewNotSpecialHome key={carro._id} carro={carro} />
            ))}
          </div>
          <button className="arrow-h right-arrow" onClick={() => scrollByRef(scrollRefRecent, 500)}>
            <img
              src="icons/arrow-right.png"
              alt="flecha derecha"
              className="arrow-img"
            />
          </button>
        </div>
      )}
      <div className="offers">
        <div className="buttons">
          <button
            id="recent-cars"
            className={`btnCar ${
              selectedButton === "Recien subidos" ? "active" : ""
            }`}
            onClick={() => handleButtonClick("Recien subidos", findNew)}
          >
            Recién subidos
          </button>
          <button
            id="offer-cars"
            className={`btnCar ${selectedButton === "Ofertas" ? "active" : ""}`}
            onClick={() => handleButtonClick("Ofertas", findofferta)}
          >
            Ver ofertas
          </button>
          <button
            id="stock-cars"
            className={`btnCar ${selectedButton === "Inventario completo" ? "active" : ""}`}
            onClick={() => handleButtonClick("Inventario completo", findStock)}
          >
            Inventario completo
          </button>
          <button
            id="buen-estado-tab"
            className={`btnCar ${selectedButton === "Buen Estado" ? "active" : ""}`}
            onClick={() => handleButtonClick("Buen Estado", findBuenEstado)}
          >
            Carros en Buen Estado
          </button>
        </div>

        <div className="condition-filter">
          <button
            id="buen-estado-cars"
            className={`btn-buen-estado ${selectedButton === "Buen Estado" ? "active" : ""}`}
            onClick={() => handleButtonClick("Buen Estado", findBuenEstado)}
          >
            Carros en Buen Estado
          </button>
        </div>

        <button className="arrow-h left-arrow" onClick={() => scrollByRef(scrollRefCars, -500)}>
          <img
            src="icons/arrow-left.png"
            alt="flecha izquierda"
            className="arrow-img"
          />
        </button>
        <div className="scroll-type" ref={scrollRefCars} data-scroll-key="home-cars">
          {carros.map((carro) => {
            if (isInventory) return <CarOverviewNotSpecialHome key={carro._id} carro={carro} />;
            if (isBuenEstado) return <CarOverviewNotSpecialHome key={carro._id} carro={carro} />;
            if (isDestacado) return <CarOverviewDestacado key={carro._id} carro={carro} />;
            if (isofferta) return <CarOverviewOferta key={carro._id} carro={carro} />;
            return <CarOverview key={carro._id} carro={carro} />;
          })}
          {isInventory && (
            <div
              ref={inventorySentinelRef}
              className="inventory-sentinel"
              aria-hidden="true"
            />
          )}
        </div>
        <button className="arrow-h right-arrow" onClick={() => scrollByRef(scrollRefCars, 500)}>
          <img
            src="icons/arrow-right.png"
            alt="flecha derecha"
            className="arrow-img"
          />
        </button>
      </div>
      <center>
        <div className="search-by-type">
          <p className="search-title">BUSCAR POR TIPO DE CARRO</p>
          <button className="arrow-h left-arrow" onClick={scrollLeft}>
            <img
              src="icons/arrow-left.png"
              alt="flecha izquierda"
              className="arrow-img"
            />
          </button>
          <div className="scroll-type" ref={scrollRef} data-scroll-key="home-types">
            <a href="/stock?tipo=Sedan" className="car-type">
              <img src="images/sedan.png" alt="" className="car" />
              <p className="car-name-type">TURISMO</p>
            </a>
            <a href="/stock?tipo=Camioneta" className="car-type">
              <img
                src="images/camioneta.png"
                alt=""
                className="car"
                id="camioneta"
              />
              <p className="car-name-type">CAMIONETA</p>
            </a>
            <a href="/stock?tipo=Pick Up" className="car-type">
              <img
                src="images/pick-up.png"
                alt=""
                className="car"
                id="pick-up"
              />
              <p className="car-name-type">PICK-UP</p>
            </a>
            <a href="/stock?tipo=Camioncito" className="car-type">
              <img
                src="images/camioncito.png"
                alt=""
                className="car"
                id="camioncito"
              />
              <p className="car-name-type">CAMIONCITO</p>
            </a>
            <a href="/stock?tipo=Camion" className="car-type">
              <img src="images/truck.png" alt="" className="car" id="camion" />
              <p className="car-name-type">CAMIÓN</p>
            </a>
            <a href="/stock?tipo=Moto" className="car-type">
              <img src="images/moto.png" alt="" className="car" id="Moto" />
              <p className="car-name-type">MOTOCICLETA</p>
            </a>
            <a href="/stock?tipo=Varios" className="car-type">
              <img src="images/varios.png" alt="" className="car" id="Varios" />
              <p className="car-name-type">VARIOS</p>
            </a>
            <a href="/stock?tipo=Cabezal" className="car-type">
              <img
                src="images/cabezal.png"
                alt=""
                className="car"
                id="cabezal"
              />
              <p className="car-name-type">CABEZAL</p>
            </a>
            <a href="/stock?tipo=Bus" className="car-type">
              <img src="images/bus.png" alt="" className="car" id="bus" />
              <p className="car-name-type">BUS</p>
            </a>
          </div>
          <button className="arrow-h right-arrow" onClick={scrollRight}>
            <img
              src="icons/arrow-right.png"
              alt="flecha derecha"
              className="arrow-img"
            />
          </button>
        </div>
      </center>
      <div className="ads-logos" id="to-logo">
        <div className="container">
          <div className="ads-container-1">
            <div className="ad-15">
              <VideoContainer />
            </div>
            <div className="ad-2"></div>
          </div>
          <LogoGallery />
          <div className="ads-container">
            <div className="ad-1">
              <VideoContainer />
            </div>
            <div className="ad-2">
              <ImageCarousel images={banners}></ImageCarousel>
            </div>
            <div className="ad-3">
              <ImageCarousel images={banners}></ImageCarousel>
            </div>
          </div>
        </div>
      </div>
      <div className="footer-movil">
        <FooterMovil />
      </div>
      <div className="footer">
        <Footer />
      </div>
    </>
  );
};

export default Home;
